import React, { useEffect, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { sendCommunication, sendGroupCommunication, getCommunications, getStudents, initiateCall } from '../services/api';

const templateMessages: Record<string, { he: string; ar: string }> = {
  payment_reminder: {
    he: 'שלום {name}, נרשם חוב על חשבונך במכללת מנארה. אנא הסדר/י את התשלום בהקדם.',
    ar: 'مرحبًا {name}، تم تسجيل دين في حسابك في كلية منارة. يرجى تسديد الدفعة في أقرب وقت.'
  },
  enrollment_confirmation: {
    he: 'שלום {name}, הרשמתך למכללת מנארה אושרה בהצלחה. ברוכ/ה הבא/ה!',
    ar: 'مرحبًا {name}، تم تأكيد تسجيلك في كلية منارة بنجاح. أهلاً وسهلاً!'
  },
  schedule_update: {
    he: 'שלום {name}, חל עדכון במערכת השעות שלך. אנא בדוק/י את המערכת המעודכנת.',
    ar: 'مرحبًا {name}، تم تحديث الجدول الزمني الخاص بك. يرجى مراجعة الجدول المحدث.'
  },
  general: {
    he: '',
    ar: ''
  }
};

export default function Communication() {
  const { t, lang } = useLang();
  const { userName } = useUser();
  const [students, setStudents] = useState<any[]>([]);
  const [comms, setComms] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('send');
  const [form, setForm] = useState({
    type: 'email', recipientId: '', subject: '', message: '', template: '',
    performedBy: ''
  });
  const [groupForm, setGroupForm] = useState({
    type: 'email', recipientIds: [] as string[], subject: '', message: '', template: '',
    performedBy: ''
  });
  const [sendMode, setSendMode] = useState<'single' | 'group'>('single');

  useEffect(() => {
    getStudents().then(res => setStudents(res.data)).catch(() => {});
    getCommunications().then(res => setComms(res.data)).catch(() => {});
    setForm(f => ({...f, performedBy: userName}));
    setGroupForm(f => ({...f, performedBy: userName}));
  }, []);

  const handleTemplateChange = (template: string, isSingle: boolean) => {
    const msg = templateMessages[template]?.[lang === 'ar' ? 'ar' : 'he'] || '';
    if (isSingle) {
      setForm({ ...form, template, message: msg });
    } else {
      setGroupForm({ ...groupForm, template, message: msg });
    }
  };

  const formatPhoneForWhatsApp = (phone: string) => {
    const cleaned = phone.replace(/[^0-9]/g, '');
    return cleaned.startsWith('0') ? '972' + cleaned.slice(1) : '972' + cleaned;
  };

  const handleSend = async () => {
    if (sendMode === 'single') {
      if (form.type === 'whatsapp' && form.recipientId) {
        const student = students.find(s => s.id === form.recipientId);
        if (student?.phone) {
          const waPhone = formatPhoneForWhatsApp(student.phone);
          const text = form.message ? encodeURIComponent(form.message.replace('{name}', `${student.firstName} ${student.lastName}`)) : '';
          window.open(`https://wa.me/${waPhone}${text ? '?text=' + text : ''}`, '_blank');
        }
      }
      if (form.type === 'phone' && form.recipientId) {
        const student = students.find(s => s.id === form.recipientId);
        if (student?.phone) {
          await initiateCall({
            phoneNumber: student.phone,
            entityType: 'student',
            entityId: student.id,
            entityName: `${student.firstName} ${student.lastName}`,
            calledBy: userName,
          });
          alert(lang === 'ar' ? 'بدأت المكالمة!' : 'השיחה התחילה!');
          getCommunications().then(res => setComms(res.data)).catch(() => {});
          return;
        }
      }
      await sendCommunication({
        ...form,
        recipientType: 'student'
      });
    } else {
      if (groupForm.type === 'whatsapp') {
        groupForm.recipientIds.forEach(rid => {
          const student = students.find(s => s.id === rid);
          if (student?.phone) {
            const waPhone = formatPhoneForWhatsApp(student.phone);
            const text = groupForm.message ? encodeURIComponent(groupForm.message.replace('{name}', `${student.firstName} ${student.lastName}`)) : '';
            window.open(`https://wa.me/${waPhone}${text ? '?text=' + text : ''}`, '_blank');
          }
        });
      }
      if (groupForm.type === 'phone') {
        for (const rid of groupForm.recipientIds) {
          const student = students.find(s => s.id === rid);
          if (student?.phone) {
            await initiateCall({
              phoneNumber: student.phone,
              entityType: 'student',
              entityId: student.id,
              entityName: `${student.firstName} ${student.lastName}`,
              calledBy: userName,
            });
          }
        }
        alert(lang === 'ar' ? 'بدأت المكالمات!' : 'השיחות התחילו!');
        getCommunications().then(res => setComms(res.data)).catch(() => {});
        return;
      }
      await sendGroupCommunication(groupForm);
    }
    alert(lang === 'ar' ? 'تم الإرسال بنجاح' : 'ההודעה נשלחה בהצלחה');
    getCommunications().then(res => setComms(res.data)).catch(() => {});
  };

  const toggleGroupRecipient = (id: string) => {
    const ids = groupForm.recipientIds.includes(id)
      ? groupForm.recipientIds.filter(r => r !== id)
      : [...groupForm.recipientIds, id];
    setGroupForm({ ...groupForm, recipientIds: ids });
  };

  return (
    <div>
      <div className="page-title">
        <span className="page-title-text">📨 {t('communication')}</span>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'send' ? 'active' : ''}`} onClick={() => setActiveTab('send')}>{t('sendMessage')}</button>
        <button className={`tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>{t('messageHistory')}</button>
      </div>

      {activeTab === 'send' && (
        <div className="page-section">
          <div className="toolbar" style={{ marginBottom: 20 }}>
            <button className={`btn ${sendMode === 'single' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setSendMode('single')}>
              הודעה בודדת
            </button>
            <button className={`btn ${sendMode === 'group' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setSendMode('group')}>
              {t('sendToGroup')}
            </button>
          </div>

          {sendMode === 'single' ? (
            <div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('messageType')}</label>
                  <select className="form-select" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                    <option value="email">{t('emailMessage')} 📧</option>
                    <option value="whatsapp">{t('whatsappMessage')} 📱</option>
                    <option value="phone">{t('phoneCall')} 📞</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('recipient')}</label>
                  <select className="form-select" value={form.recipientId} onChange={e => setForm({...form, recipientId: e.target.value})}>
                    <option value="">-- בחר --</option>
                    {students.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('template')}</label>
                  <select className="form-select" value={form.template} onChange={e => handleTemplateChange(e.target.value, true)}>
                    <option value="">-- בחר תבנית --</option>
                    <option value="payment_reminder">{t('templates.payment_reminder')}</option>
                    <option value="enrollment_confirmation">{t('templates.enrollment_confirmation')}</option>
                    <option value="schedule_update">{t('templates.schedule_update')}</option>
                    <option value="general">{t('templates.general')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('subject')}</label>
                  <input className="form-input" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('message')}</label>
                <textarea className="form-textarea" value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={5} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('actionPerformer')}</label>
                <input className="form-input" value={form.performedBy} onChange={e => setForm({...form, performedBy: e.target.value})} />
              </div>
            </div>
          ) : (
            <div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('messageType')}</label>
                  <select className="form-select" value={groupForm.type} onChange={e => setGroupForm({...groupForm, type: e.target.value})}>
                    <option value="email">{t('emailMessage')} 📧</option>
                    <option value="whatsapp">{t('whatsappMessage')} 📱</option>
                    <option value="phone">{t('phoneCall')} 📞</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('template')}</label>
                  <select className="form-select" value={groupForm.template} onChange={e => handleTemplateChange(e.target.value, false)}>
                    <option value="">-- בחר תבנית --</option>
                    <option value="payment_reminder">{t('templates.payment_reminder')}</option>
                    <option value="enrollment_confirmation">{t('templates.enrollment_confirmation')}</option>
                    <option value="schedule_update">{t('templates.schedule_update')}</option>
                    <option value="general">{t('templates.general')}</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">בחר נמענים ({groupForm.recipientIds.length} נבחרו)</label>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 8, padding: 8 }}>
                  {students.map(s => (
                    <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={groupForm.recipientIds.includes(s.id)}
                        onChange={() => toggleGroupRecipient(s.id)}
                      />
                      {s.firstName} {s.lastName} (<span dir="ltr">{s.email}</span>)
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('subject')}</label>
                <input className="form-input" value={groupForm.subject} onChange={e => setGroupForm({...groupForm, subject: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('message')}</label>
                <textarea className="form-textarea" value={groupForm.message} onChange={e => setGroupForm({...groupForm, message: e.target.value})} rows={5} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('actionPerformer')}</label>
                <input className="form-input" value={groupForm.performedBy} onChange={e => setGroupForm({...groupForm, performedBy: e.target.value})} />
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button className="btn btn-primary" onClick={handleSend}>
              {t('send')} ✉️
            </button>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="page-section">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>סוג</th>
                <th>{t('recipient')}</th>
                <th>{t('subject')}</th>
                <th>{t('message')}</th>
                <th>{t('date')}</th>
                <th>{t('performedBy')}</th>
              </tr>
            </thead>
            <tbody>
              {comms.map(c => {
                const s = students.find(st => st.id === c.recipientId);
                return (
                  <tr key={c.id}>
                    <td><span className={`badge ${c.type === 'email' ? 'badge-blue' : c.type === 'phone' ? 'badge-gold' : 'badge-green'}`}>
                      {c.type === 'email' ? '📧' : c.type === 'phone' ? '📞' : '📱'} {c.type}
                    </span></td>
                    <td>{s ? `${s.firstName} ${s.lastName}` : c.recipientId}</td>
                    <td>{c.subject}</td>
                    <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.message}</td>
                    <td>{new Date(c.sentAt).toLocaleDateString('he-IL')}</td>
                    <td>{c.performedBy || '-'}</td>
                  </tr>
                );
              })}
              {comms.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
